import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "nah_cookie_consent_v1";

/**
 * Cookie consent banner referenced by Privacy Policy section 6.
 *
 * Three choices: "Accept all" (essential + analytics), "Essential only"
 * (auth + anti-cheat), or dismiss without selecting (treated as essential
 * only — minimum viable consent so the site remains usable).
 *
 * Stored in localStorage so the banner stays dismissed across visits.
 * Bump STORAGE_KEY (e.g. _v2) if the consent categories change.
 *
 * Mounted once at the App root so it appears on EVERY page until dismissed.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (_e) {
      // localStorage may be blocked (e.g., very strict private browsing).
      // Show the banner anyway — choice persists for the session via state.
      setVisible(true);
    }
  }, []);

  const persist = (level) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ level, at: new Date().toISOString() }),
      );
    } catch (_e) { /* noop */ }
    // Tell the rest of the app what the user picked. Analytics scripts
    // (none today) can listen for this event before initializing.
    window.dispatchEvent(new CustomEvent("nah:consent", { detail: { level } }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:max-w-md z-[80] surface-glass border border-white/15 shadow-[0_18px_48px_rgba(0,0,0,0.55)]"
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base mb-1">Cookies</div>
            <p className="text-xs text-white/70 leading-relaxed">
              We use cookies for sign-in, anti-cheat, and gameplay
              preferences. Optional analytics help us tune balance and
              difficulty. See our{" "}
              <Link
                to="/privacy"
                className="text-[#FFCC00] underline underline-offset-2 hover:text-[#FFD633]"
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <button
            onClick={() => persist("essential")}
            className="text-white/30 hover:text-white"
            aria-label="Dismiss cookie banner"
            data-testid="cookie-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => persist("all")}
            className="flex-1 min-w-[120px] px-3 py-2 bg-[#FFCC00] text-black font-heading text-xs uppercase tracking-[0.2em] hover:bg-[#FFD633] transition-colors"
            data-testid="cookie-accept-all"
          >
            Accept all
          </button>
          <button
            onClick={() => persist("essential")}
            className="flex-1 min-w-[120px] px-3 py-2 border border-white/15 text-white/80 font-heading text-xs uppercase tracking-[0.2em] hover:border-white/40 hover:text-white transition-colors"
            data-testid="cookie-essential-only"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
